import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { VideoTrigger } from "../components/VideoPlayerModal";
import { CategoryGrid } from "../components/CategoryGrid";
import { CategoryChipBar } from "../components/CategoryChipBar";
import { sortCategoriesByOrder } from "../lib/categoryOrder";
import { useSettings } from "../context/SettingsContext";
import { ServiceGridLoading } from "../components/ServiceCardSkeleton";
import { getServices, optimizeImageUrl } from "../lib/servicesCache";
import { PageBackdrop } from "../components/PageBackdrop";

const WORD_LIMIT = 20;

function ServiceDescription({ text, testId }) {
    const [open, setOpen] = useState(false);
    if (!text) return null;
    const words = text.trim().split(/\s+/);
    const isLong = words.length > WORD_LIMIT;
    const visible = isLong && !open ? words.slice(0, WORD_LIMIT).join(" ") + "…" : text;
    return (
        <p className="text-[0.78rem] leading-snug text-[#7A5262] mt-1.5 flex-1">
            {visible}
            {isLong && (
                <>
                    {" "}
                    <button
                        type="button"
                        onClick={() => setOpen((v) => !v)}
                        className="text-[#8F622C] hover:text-[#3D0C1F] uppercase tracking-[0.18em] text-[0.6rem] underline-offset-2 hover:underline"
                        data-testid={testId}
                    >
                        {open ? "Read less" : "Read more"}
                    </button>
                </>
            )}
        </p>
    );
}

export default function ServicesPage() {
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchParams] = useSearchParams();
    const [activeCategory, setActiveCategory] = useState(searchParams.get("cat") || "All");
    const { settings } = useSettings();
    useEffect(() => {
        getServices()
            .then((data) => setServices(data))
            .finally(() => setLoading(false));
    }, []);
    // Sync when user deep-links with ?cat=...
    useEffect(() => {
        const cat = searchParams.get("cat");
        if (cat) setActiveCategory(cat);
    }, [searchParams]);

    const grouped = useMemo(() => services.reduce((acc, s) => {
        (acc[s.category] = acc[s.category] || []).push(s);
        return acc;
    }, {}), [services]);

    const orderedEntries = useMemo(
        () => sortCategoriesByOrder(Object.entries(grouped), settings.category_order),
        [grouped, settings.category_order]
    );

    const visibleEntries = activeCategory === "All"
        ? orderedEntries
        : orderedEntries.filter(([cat]) => cat === activeCategory);

    const renderCard = (s) => {
        const op = Number(s.original_price) || 0;
        const price = Number(s.price) || 0;
        const hasDiscount = op > price && price > 0;
        const pct = hasDiscount ? Math.round(((op - price) / op) * 100) : 0;
        return (
        <div key={s.id} className="card-luxe grain group flex flex-col" data-testid={`service-${s.id}`}>
            {s.image && (
                <div className="aspect-[16/9] overflow-hidden relative">
                    <img src={optimizeImageUrl(s.image, 480)} alt={s.name} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    {hasDiscount && (
                        <div className="absolute top-2 right-2 bg-[#6B1535] text-[#FFF8F2] px-2 py-0.5 text-[0.55rem] tracking-[0.2em] uppercase font-semibold" data-testid={`service-discount-badge-${s.id}`}>-{pct}% OFF</div>
                    )}
                </div>
            )}
            <div className="p-3 flex-1 flex flex-col">
                <h3 className="font-serif-display text-base leading-tight text-[#3D0C1F]">{s.name}</h3>
                <ServiceDescription text={s.description} testId={`service-readmore-${s.id}`} />
                {s.video_enabled && (
                    <div className="mt-2">
                        <VideoTrigger url={s.video_url} testId={`service-video-${s.id}`} />
                    </div>
                )}
                <div className="flex justify-between items-center mt-2 pt-2 border-t border-[#6B1535]/10">
                    <span className="flex items-baseline gap-2">
                        <span className="text-[#8F622C] text-sm font-bold">S${s.price}</span>
                        {hasDiscount && (
                            <span className="text-[0.65rem] text-[#8A6574] line-through" data-testid={`service-original-price-${s.id}`}>S${op}</span>
                        )}
                    </span>
                    {settings.show_service_duration !== false && (
                        <span className="text-[0.65rem] text-[#8A6574]">{s.duration_min} min</span>
                    )}
                </div>
                <Link to={`/book?service=${s.id}`} className="btn-gold mt-2 justify-center py-1.5 text-[0.65rem] font-bold tracking-[0.2em]" data-testid={`book-${s.id}`}>
                    Book Now <ArrowUpRight size={10} />
                </Link>
            </div>
        </div>
        );
    };

    const chipDefs = [
        { value: "All", label: "All", count: services.length },
        ...orderedEntries.map(([cat]) => ({ value: cat, label: cat, count: grouped[cat]?.length || 0 })),
    ];

    return (
        <div className="relative isolate min-h-screen pt-32 pb-32 grain" data-testid="services-page">
            <PageBackdrop testid="services-bg" />
            <div className="max-w-7xl mx-auto px-6 lg:px-12">
                <div className="text-overline">{settings.full_menu_overline || "Full Menu"}</div>
                <h1 className="font-serif-display text-5xl sm:text-7xl text-[#3D0C1F] mt-3">{settings.full_menu_heading || "All Services."}</h1>
                <p className="text-[#7A5262] mt-3 max-w-xl">{settings.full_menu_subtitle || "From precision men's cuts to bridal couture — explore our curated menu."}</p>

                {chipDefs.length > 1 && (
                    <div className="mt-10">
                        <CategoryChipBar
                            chips={chipDefs}
                            active={activeCategory}
                            onSelect={setActiveCategory}
                            testIdPrefix="chip"
                            barTestId="category-chip-bar"
                        />
                    </div>
                )}

                <div className="space-y-20 mt-12">
                    {loading ? (
                        <ServiceGridLoading count={8} headline="Curating your experience" />
                    ) : (
                        <>
                            {visibleEntries.map(([cat, items]) => (
                                <CategoryGrid
                                    key={cat}
                                    category={cat}
                                    items={items}
                                    perPage={activeCategory === "All" ? (settings.services_per_category || 4) : items.length}
                                    renderItem={renderCard}
                                />
                            ))}
                            {visibleEntries.length === 0 && (
                                <p className="text-[#7A5262] text-sm">No services in this category yet.</p>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
